import React, { useContext, useState, useEffect } from "react"
import { useMutation, useQuery } from "@apollo/client"
import _ from "lodash"
import { Link } from "react-router-dom"
import { InputSwitch } from "primereact/inputswitch"
import { LoaderMedium } from "../../components/Loaders"
import { ElementLoading } from "../../components/ElementLoading"
import { UserContext } from "../../context/userContext"
import {
  CHANGE_STATUS_PROGRAM,
  GET_PROGRAMS_BY_ENTERPRISE_ID,
  GET_ORGANIZATIONS,
} from "./queries"
import withDataFetching from "../../components/DataTable/withDataFetching"
import PageHeader from "../../components/PageHeader"
import GenericTable from "../../components/DataTable/GenericTable"
import { useFilters } from "../../components/Filters/useFilters"
import FilterHeader from "../../components/DataTable/FilterHeader"

const ProgramsList = ({ data }) => {
  const userCtx = useContext(UserContext)
  const [changeStatusProgram] = useMutation(CHANGE_STATUS_PROGRAM)
  const [loadingStatus, setLoadingStatus] = useState({})
  const [organizations, setOrganizations] = useState([])

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
  } = useFilters()

  const { data: orgData, loading: orgsLoading } = useQuery(GET_ORGANIZATIONS, {
    variables: {
      staff_id: userCtx.session.user?.staff_id,
      organization_id: userCtx.session.user?.organization_id,
    },
    skip: !userCtx.session.user.staff_id,
    fetchPolicy: "network-only",
  })

  useEffect(() => {
    if (orgData) {
      const organizationsOptions = _.map(
        orgData.organizations,
        ({ organization_id, organization_name }) => ({
          label: organization_name,
          value: organization_name,
        })
      )
      setOrganizations([{ label: "All", value: "" }, ...organizationsOptions])
    }
  }, [orgData])

  const handleOnChange = async (
    value,
    program_identity_id,
    organization_id
  ) => {
    setLoadingStatus((prev) => ({ ...prev, [program_identity_id]: true }))
    try {
      await changeStatusProgram({
        variables: {
          staff_id: userCtx?.session.user.staff_id,
          staff_alias: userCtx?.session.user.staff_alias,
          organization_id,
          program_identity_id,
          program_id_status: value === true ? "ACTIVE" : "INACTIVE",
        },
        skip: !userCtx?.session.user.staff_id,
        refetchQueries: [
          {
            query: GET_PROGRAMS_BY_ENTERPRISE_ID,
            variables: {
              staff_id: userCtx?.session.user.staff_id,
              enterprise_id: userCtx?.session.user.enterprise_id,
              organization_id: userCtx?.session.user.organization_id,
            },
          },
        ],
        fetchPolicy: "no-cache",
      })
    } catch (error) {
      console.error("Error changing status:", error)
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [program_identity_id]: false }))
    }
  }

  if (orgsLoading) return <LoaderMedium />

  const programs = _.map(data.programs, (program) => {
    const organizations = program.organizations
      .split(",")
      .map((org) => _.unescape(org.trim()))

    return {
      ...program,
      organization_names: organizations,
      enterprise_managed: program.program_id_enterprise_managed ? "Yes" : null,
      gpra: program.program_id_gpra ? "Active" : "Inactive",
      program_id_code: program.program_id_code || null,
      status: (
        <ElementLoading
          status={program.program_id_status}
          item={program.program_identity_id}
          parentCall={(value) =>
            handleOnChange(
              value,
              program.program_identity_id,
              program.organization_id
            )
          }
        >
          <InputSwitch
            checked={program.program_id_status === "ACTIVE"}
            disabled={loadingStatus[program.program_identity_id]}
          />
        </ElementLoading>
      ),
      edit: (
        <Link
          to={`/enterprise/form/program/1/${program.program_identity_id}`}
          className="btn"
        >
          Edit <i className="pi pi-pencil"></i>
        </Link>
      ),
      formatted_organizations: organizations.join(", "),
      organization_names_display: organizations.join(", "),
    }
  })

  const filterConfig = [
    {
      label: "Organization",
      value: filters.organization_names?.value || "",
      options: organizations,
      onChange: (e) => onFilterChange("organization_names", e.value),
      onClear: () => onFilterChange("organization_names", null),
      placeholder: "Select an Organization",
      enableFilter: true,
    },
    {
      label: "GPRA",
      value: filters.gpra?.value || "",
      options: [
        { label: "All", value: "" },
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" },
      ],
      onChange: (e) => onFilterChange("gpra", e.value),
      onClear: () => onFilterChange("gpra", null),
      placeholder: "Select GPRA Status",
    },
    {
      label: "Status",
      value: filters.program_id_status?.value || "",
      options: [
        { label: "All", value: "" },
        { label: "Active", value: "ACTIVE" },
        { label: "Inactive", value: "INACTIVE" },
      ],
      onChange: (e) => onFilterChange("program_id_status", e.value),
      onClear: () => onFilterChange("program_id_status", null),
      placeholder: "Select Status",
    },
  ]

  const columns = [
    { field: "program_id_name", header: "Program", sortable: true },
    {
      field: "enterprise_managed",
      header: "Enterprise Managed",
      sortable: true,
    },
    {
      field: "organization_names_display",
      header: "Organization(s)",
      body: (rowData) => (
        <span>
          {rowData.organization_names_display.split(",").map((org, index) => (
            <span key={index}>
              {org.trim()}
              <br />
            </span>
          ))}
        </span>
      ),
    },
    {
      field: "program_id_code",
      header: "Billing or Service Code",
      sortable: true,
    },
    { field: "gpra", header: "GPRA", sortable: true },
    { field: "status", header: "Status", sortable: true },
    { field: "edit", header: "Edit", sortable: true },
  ]

  return (
    <div className="main-content">
      <PageHeader
        title="Manage Program ID"
        actions={[
          { to: "/enterprise/form/program/1/", label: "Create new Program ID" },
        ]}
      />
      <GenericTable
        data={programs}
        columns={columns}
        filters={filters}
        header={
          <FilterHeader
            filters={filterConfig}
            globalFilterValue={globalFilterValue}
            onGlobalFilterChange={onGlobalFilterChange}
            clearGlobalFilter={clearGlobalFilter}
            clearAllFilters={clearAllFilters}
          />
        }
        paginator
        rows={50}
        sortField="program_id_name"
        sortOrder={1}
      />
    </div>
  )
}

const WrappedProgramsList = (props) => {
  const userCtx = useContext(UserContext)
  const variables = {
    staff_id: userCtx?.session.user.staff_id,
    enterprise_id: userCtx?.session.user.enterprise_id,
    organization_id: userCtx?.session.user.organization_id,
  }

  const EnhancedProgramsList = withDataFetching(
    ProgramsList,
    GET_PROGRAMS_BY_ENTERPRISE_ID
  )

  return <EnhancedProgramsList {...props} variables={variables} />
}

export default WrappedProgramsList
